"use client";
import { ButtonTypes, ComponentProps, IconPositions } from "@/baseComponents";
import { MediaImage } from "@/components/atoms/media";
import { MediaImageData } from "@/components/atoms/media/MediaImage";
import { ButtonVariants, CardTypes, Cultures } from "@/types/enums";
import { buildImageUrl } from "@/utils/imageSharpUrlBuilder";
import {
  Box,
  CardBody,
  chakra,
  Card as ChakraCard,
  Divider,
  Heading,
  Text,
} from "@chakra-ui/react";
import { decode } from "html-entities";
import DOMPurify from "isomorphic-dompurify";
import { ContactIconProps, ContactLinks } from "../contactLinks";

export interface ContactCardProps extends ComponentProps {
  cardType: CardTypes;
  title: string;
  text?: string;
  contactLinks?: ContactIconProps[];
  mediaData?: MediaImageData;
  isThreeGrid?: boolean;
  culture?: Cultures;
}

export default chakra(ContactCard);
function ContactCard({
  cardType,
  title,
  text,
  contactLinks,
  mediaData,
  isThreeGrid = false,
  culture = Cultures["en-US"],
  theme = "brand",
  ...props
}: ContactCardProps) {
  const sanitizedTitle = DOMPurify.sanitize(title.replace("&nbsp;", ""), {
    ALLOWED_TAGS: [],
  });

  const imageSize = isThreeGrid
    ? { base: "5rem", lg: "5rem", xl: "9rem", "2xl": "17rem" }
    : { base: "5rem", lg: "9rem", xl: "17rem" };

  return (
    <ChakraCard
      sx={{
        padding: { base: 4, md: 4, lg: 0 },
        borderRadius: { base: "1rem", lg: 0 },
        boxShadow: "none",
        backgroundColor: { base: `${theme}.grey`, lg: "transparent" },
      }}
    >
      <CardBody
        display={{ base: "grid", lg: "flex" }}
        gridTemplateColumns={{ base: mediaData && "5rem minmax(0, 1fr)" }}
        flexDirection="row"
        gap={{ base: 5, lg: 10 }}
        height="100%"
        minWidth={0}
        p={0}
      >
        {mediaData && (
          <MediaImage
            {...mediaData}
            viewportWidthFactor={{ base: 0.2, md: 0.25 }}
            cropSizeAlias={{ base: "square" }}
            hasBorderRadius={true}
            imageUrlBuilder={buildImageUrl}
            width={imageSize}
            height={imageSize}
            minWidth={imageSize}
            flexShrink={0}
          />
        )}

        <Box
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          width="100%"
          minWidth={0}
          gap={4}
          py={{ base: 0, lg: 5 }}
        >
          <Box>
            {title && (
              <Heading
                as="h2"
                sx={{
                  textStyle: { base: "heading.sm", md: "heading.md", lg: "heading.md"},
                  fontWeight: 300,
                  color: "gray.800",
                }}
              >
                {decode(sanitizedTitle)}
              </Heading>
            )}
            <Text
              sx={{
                textStyle: { base: "display.x4s", md: "display.x4s" },
                color: `${theme}.aubergine`,
              }}
            >
              {text}
            </Text>
          </Box>

          {contactLinks && (
            <>
              <Divider
                orientation="horizontal"
                display={{ base: "none", lg: "block" }}
                width="80px"
              />
              <ContactLinks
                display={{ base: "none", lg: "flex" }}
                items={contactLinks}
                flexDir="column"
                alignItems="start"
                gap="1rem"
                wordBreak="break-word"
                buttonProps={{
                  iconPosition: IconPositions.Left,
                  buttonType: ButtonTypes.Default,
                  buttonVariant: ButtonVariants.ContactLinks,
                }}
              />
            </>
          )}
        </Box>
        {contactLinks && (
          <ContactLinks
            display={{ base: "flex", lg: "none" }}
            gridColumn="1 / -1"
            items={contactLinks}
            flexDir="column"
            alignItems="start"
            gap="1rem"
            wordBreak="break-word"
            buttonProps={{
              iconPosition: IconPositions.Left,
              buttonType: ButtonTypes.Default,
              buttonVariant: ButtonVariants.ContactLinks,
            }}
          />
        )}
      </CardBody>
    </ChakraCard>
  );
}
